import { useEffect, useRef, useState } from 'react';

// Links in chat are never opened. A tap copies the link, so nobody lands on a
// site they didn't mean to visit. Matches http(s)://, www. and bare domains.
const URL_RE = /((?:https?:\/\/|www\.)[^\s<>"]+|\b[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|org|net|io|co|club|app|dev|me|in|uk|de|ai|gg|ly|tv|xyz|info|link|site|online|store|blog)(?:\/[^\s<>"]*)?)/gi;
// Trailing punctuation usually ends the sentence, not the link.
const TRAIL_RE = /[.,:;!?'")\]]+$/;

type Part = { text: string; url?: boolean };

export const splitLinks = (text: string): Part[] => {
  const parts: Part[] = [];
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    const raw = m[0];
    const url = raw.replace(TRAIL_RE, '');
    if (!url) continue;
    if (start > last) parts.push({ text: text.slice(last, start) });
    parts.push({ text: url, url: true });
    last = start + url.length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
};

const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older browsers: copy through a hidden text box.
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      el.remove();
      return ok;
    } catch {
      return false;
    }
  }
};

const ChatLink = ({ url }: { url: string }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return (
    <span className="relative inline">
      <button
        type="button"
        onClick={async (e) => {
          e.stopPropagation();
          if (await copyText(url)) {
            setCopied(true);
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), 1600);
          }
        }}
        title="Tap to copy this link"
        aria-label={`Copy link: ${url}`}
        className="inline break-all text-left underline decoration-dotted underline-offset-4 decoration-current/60 hover:decoration-solid cursor-copy"
      >
        {url}
      </button>
      {copied && (
        <span
          role="status"
          className="absolute left-1/2 -translate-x-1/2 -top-8 whitespace-nowrap rounded-full bg-black/80 text-white text-[0.6875rem] px-2.5 py-1 shadow-lg pointer-events-none"
        >
          Link copied ✓
        </span>
      )}
    </span>
  );
};

// Chat message text, with links shown as tap-to-copy.
export default function ChatText({ text }: { text: string }) {
  const parts = splitLinks(text);
  if (!parts.some(p => p.url)) return <>{text}</>;
  return <>{parts.map((p, i) => (p.url ? <ChatLink key={i} url={p.text} /> : <span key={i}>{p.text}</span>))}</>;
}

export { copyText };
